import React, { useEffect, useId, useRef, useState } from 'react';
import { observeVisibility, pointer, subscribeFrame } from './mascot-ticker';

/**
 * The Blueticks AI face — ported from instinct-wa.com `src/components/Mascot.tsx`;
 * change shape/motion constants in both files together.
 *
 * A soft, slightly imperfect blob with two slanted pill eyes. No mouth, no
 * details — all of the life is in the eyes.
 *
 *   idle     — blink (and, at level="full", follow the cursor / wander)
 *   thinking — eyes drift up and scan (the agent is working)
 *   happy    — smile-squint (a finished task; also on hover at level="full")
 *   excited  — eyes wide open and a touch bigger, looking up (an invitation: "make me!")
 *   asleep   — closed-eye arcs, face dimmed (--ai-mascot-asleep-opacity), slow deep breathing (a paused agent)
 *
 * `level` sets how alive it is:
 *   full    — everything above; ≤ 1 per screen (hero, the active chat)
 *   ambient — blink + breathe + mood only; no pointer work (lists, avatars)
 *   static  — the still pose for the mood, no animation loop
 * `prefers-reduced-motion` forces static.
 *
 * Colour comes from `tone` (any CSS colour; per-agent tones via `toneForAgent`,
 * or use <AgentFace>). Omitted, it is the brand face: instinct's white head with
 * ink eyes on dark, the inverse on light (`--ai-mascot-head*` / `--ai-mascot-eye`).
 *
 * All animated faces share ONE rAF loop (mascot-ticker.ts), and only `full`
 * faces keep the one shared pointer listener alive. Frames write SVG transforms
 * directly — no React render per frame. Offscreen faces drop out of the loop.
 *
 * Deliberate departures from instinct (the rest is 1:1):
 *   - the brand head follows the theme (instinct is dark-only); light = ink head;
 *   - a tinted head's gradient bottom mixes toward `--ai-mascot-shade` (darker on
 *     light, lighter on dark) instead of instinct's 0.78 opacity, which goes muddy
 *     over a dark ground;
 *   - the glow halo is tinted mint/tone (instinct's white halo vanishes on light);
 *   - the `asleep` mood and the `level` gate are new.
 */
export type MascotMood = 'idle' | 'thinking' | 'happy' | 'asleep' | 'excited';
export type MascotLevel = 'full' | 'ambient' | 'static';

export interface MascotProps {
  /** Rendered width and height in px. */
  size?: number;
  mood?: MascotMood;
  /** Head colour (any CSS colour). Default: the brand tone token. */
  tone?: string;
  /** How alive the face is. Default `ambient`. */
  level?: MascotLevel;
  /** Soft halo behind the head (hero usage). */
  glow?: boolean;
  className?: string;
}

export const HEAD_PATH =
  'M100 6C141 5 176 28 189 64C200 95 196 132 176 158C155 186 122 197 92 195C58 192 28 174 14 143C1 113 4 76 24 48C42 22 69 7 100 6Z';
export const EYE_W = 28;
export const EYE_H = 56;
export const EYES = [
  { cx: 70, cy: 90, rot: 18 },
  { cx: 126, cy: 99, rot: 18 },
];
const EYE_INK = '#0a0b0d';
/** Eye height factor when asleep, under the closed-eye arcs. */
const LID_CLOSED = 0.1;
/** Closed eye: a downward arc per eye. 14 units = 2.5px at 36px. */
const ARC_STROKE = 14;
export const arcPath = (e: { cx: number; cy: number }) => `M${e.cx - 17} ${e.cy - 3}Q${e.cx} ${e.cy + 15} ${e.cx + 17} ${e.cy - 3}`;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** The whole animatable state of a face; one instance per mounted Mascot. */
interface Pose {
  lookX: number; // −1..1
  lookY: number; // −1..1
  eyeScale: number; // blink × lid × squint, 0..1
  squint: number; // 0 open .. 1 happy
  wide: number; // 0 normal .. 1 excited (bigger, wide-open eyes)
  sleep: number; // 0 awake .. 1 eyes shut (arcs shown, face dimmed)
  bob: number;
  breathe: number;
}

function staticPose(mood: MascotMood): Pose {
  const squint = mood === 'happy' ? 1 : 0;
  return {
    lookX: 0,
    lookY: mood === 'thinking' ? -0.55 : mood === 'asleep' ? 0.3 : mood === 'excited' ? -0.35 : 0,
    eyeScale: (mood === 'asleep' ? LID_CLOSED : 1) * (1 - squint * 0.55),
    squint,
    wide: mood === 'excited' ? 1 : 0,
    sleep: mood === 'asleep' ? 1 : 0,
    bob: 0,
    breathe: 1,
  };
}

const eyesTransform = (p: Pose) => `translate(${(p.lookX * 14).toFixed(2)} ${(p.lookY * 11).toFixed(2)})`;

/** Excited eyes grow up to 20% (both axes; the blink/lid factor stays on y). */
const eyeWiden = (p: Pose) => 1 + p.wide * 0.2;

const eyeTransform = (p: Pose, i: number) => {
  const e = EYES[i];
  // The happy squint pushes the eyes up a touch as they close.
  return `translate(${e.cx} ${e.cy - p.squint * 4}) rotate(${e.rot}) scale(${eyeWiden(p) === 1 ? 1 : eyeWiden(p).toFixed(3)} ${(p.eyeScale * eyeWiden(p)).toFixed(3)}) translate(${-e.cx} ${-e.cy})`;
};

// Head: breathing + a slight lean toward where it looks.
const headTransform = (p: Pose) =>
  `translate(100 ${(100 + p.bob).toFixed(2)}) rotate(${(p.lookX * 3).toFixed(2)}) scale(${(p.breathe + p.squint * 0.02).toFixed(4)} ${(p.breathe * (1 - p.squint * 0.02)).toFixed(4)}) translate(-100 -100)`;

// Asleep dims the face to --ai-mascot-asleep-opacity (per theme: a strong dim goes muddy on dark).
const faceOpacity = (p: Pose) => `calc(1 - (1 - var(--ai-mascot-asleep-opacity)) * ${p.sleep.toFixed(3)})`;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(REDUCED_MOTION_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.(REDUCED_MOTION_QUERY);
    if (!mq) return undefined;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export const Mascot: React.FC<MascotProps> = ({
  size = 120,
  mood = 'idle',
  tone,
  level: requestedLevel = 'ambient',
  glow = false,
  className,
}) => {
  const headTop = tone ?? 'var(--ai-mascot-head)';
  const headBottom = tone ? `color-mix(in srgb, ${tone}, var(--ai-mascot-shade))` : 'var(--ai-mascot-head-end)';
  const eyeColor = tone ? EYE_INK : 'var(--ai-mascot-eye)';
  // SVG ids are document-global (per shadow root), and separate React roots can
  // hand out the same useId. Keying the id on the tone as well means a collision
  // can only ever be between two identical gradients, so every face keeps its colour.
  const uid = `${useId()}-${tone ?? 'brand'}`.replace(/[^a-zA-Z0-9_-]/g, '');
  const svgRef = useRef<SVGSVGElement | null>(null);
  const headRef = useRef<SVGGElement | null>(null);
  const eyesRef = useRef<SVGGElement | null>(null);
  const eyeRefs = useRef<(SVGRectElement | null)[]>([]);
  const openEyesRef = useRef<SVGGElement | null>(null);
  const arcsRef = useRef<SVGGElement | null>(null);
  const moodRef = useRef<MascotMood>(mood);
  moodRef.current = mood;

  const reducedMotion = usePrefersReducedMotion();
  const level: MascotLevel = reducedMotion ? 'static' : requestedLevel;
  // While animating the loop owns the transforms, so the JSX keeps the pose it
  // was mounted with (a mood change must not snap the face back for a frame).
  const [mountMood] = useState(mood);
  const pose = staticPose(level === 'static' ? mood : mountMood);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || level === 'static') return undefined;
    const full = level === 'full';

    const p = staticPose(moodRef.current);
    const target = { x: p.lookX, y: p.lookY };
    let lid = moodRef.current === 'asleep' ? LID_CLOSED : 1;
    let pointerSeen = pointer.t;
    let nextWander = 0;
    let blinkStart = -1;
    let nextBlink = performance.now() + 1200 + Math.random() * 2500;
    let doubleBlink = false;
    let lastFrame = 0;
    let hover = false;

    const write = (q: Pose) => {
      eyesRef.current?.setAttribute('transform', eyesTransform(q));
      eyeRefs.current.forEach((el, i) => el?.setAttribute('transform', eyeTransform(q, i)));
      openEyesRef.current?.setAttribute('opacity', (1 - q.sleep).toFixed(3));
      arcsRef.current?.setAttribute('opacity', q.sleep.toFixed(3));
      headRef.current?.setAttribute('transform', headTransform(q));
      headRef.current?.style.setProperty('opacity', faceOpacity(q));
    };

    const update = (now: number) => {
      const m = moodRef.current;
      // Back from a hidden tab or from offscreen: never open on an overdue blink.
      if (now - lastFrame > 500) {
        blinkStart = -1;
        nextBlink = now + 1200 + Math.random() * 2000;
      }
      lastFrame = now;

      if (m === 'thinking') {
        // Look up and scan slowly left↔right, like reading.
        target.x = Math.sin(now / 700) * 0.75;
        target.y = -0.55 + Math.sin(now / 1300) * 0.1;
      } else if (m === 'asleep' || !full) {
        const rest = staticPose(m);
        target.x = rest.lookX;
        target.y = rest.lookY;
      } else if (pointer.t > pointerSeen) {
        pointerSeen = pointer.t;
        const r = svg.getBoundingClientRect();
        const reach = Math.max(r.width * 3, 360);
        target.x = clamp((pointer.x - (r.left + r.width / 2)) / reach, -1, 1);
        target.y = clamp((pointer.y - (r.top + r.height / 2)) / reach, -1, 1);
      } else if (now - pointer.t > 2600 && now > nextWander) {
        // Cursor idle → glance somewhere on our own.
        target.x = (Math.random() * 2 - 1) * 0.8;
        target.y = (Math.random() * 2 - 1) * 0.5;
        nextWander = now + 1200 + Math.random() * 2200;
      }

      const speed = m === 'asleep' ? 0.04 : m === 'thinking' ? 0.08 : 0.16;
      p.lookX = lerp(p.lookX, target.x, speed);
      p.lookY = lerp(p.lookY, target.y, speed);

      lid = lerp(lid, m === 'asleep' ? LID_CLOSED : 1, 0.08);
      p.sleep = clamp((1 - lid) / (1 - LID_CLOSED), 0, 1);
      let blink = 1;
      if (blinkStart < 0 && now > nextBlink && (m === 'idle' || m === 'thinking')) {
        blinkStart = now;
        doubleBlink = Math.random() < 0.2;
      }
      if (blinkStart >= 0) {
        const dur = doubleBlink ? 420 : 170;
        const t = (now - blinkStart) / dur;
        if (t >= 1) {
          blinkStart = -1;
          nextBlink = now + 2200 + Math.random() * 3800;
        } else {
          const phase = doubleBlink ? (t * 2) % 1 : t;
          blink = 1 - Math.sin(phase * Math.PI) * 0.92;
        }
      }

      p.squint = lerp(p.squint, m === 'happy' || (hover && m !== 'asleep' && m !== 'excited') ? 1 : 0, 0.14);
      p.wide = lerp(p.wide, m === 'excited' ? 1 : 0, 0.2);
      p.eyeScale = blink * lid * (1 - p.squint * 0.55);
      p.breathe = m === 'asleep' ? 1 + Math.sin(now / 1500) * 0.03 : 1 + Math.sin(now / 900) * 0.012;
      p.bob = m === 'thinking' ? Math.sin(now / 260) * 1.6 : 0;

      write(p);
    };

    const onEnter = () => {
      hover = true;
    };
    const onLeave = () => {
      hover = false;
    };
    if (full) {
      svg.addEventListener('pointerenter', onEnter);
      svg.addEventListener('pointerleave', onLeave);
    }

    let unsubscribe: (() => void) | null = null;
    const stopObserving = observeVisibility(svg, (visible) => {
      if (visible && !unsubscribe) unsubscribe = subscribeFrame(update, full);
      else if (!visible && unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
    });

    return () => {
      stopObserving();
      // Leaving animation (reduced motion on, level → static, unmount): settle on the rest
      // pose — React won't rewrite attributes whose JSX value did not change.
      write(staticPose(moodRef.current));
      unsubscribe?.();
      svg.removeEventListener('pointerenter', onEnter);
      svg.removeEventListener('pointerleave', onLeave);
    };
  }, [level]);

  return (
    <div
      className={className}
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
      data-testid="mascot"
      data-mood={mood}
      data-tone={tone ?? 'brand'}
      data-level={level}
      aria-hidden
    >
      {glow && (
        <div
          style={{
            position: 'absolute',
            inset: '-60%',
            borderRadius: '50%',
            background: `radial-gradient(circle, color-mix(in srgb, ${tone ?? 'var(--ai-accent)'} 14%, transparent) 0%, transparent 65%)`,
            filter: 'blur(8px)',
            pointerEvents: 'none',
          }}
        />
      )}
      <svg
        ref={svgRef}
        viewBox="0 0 200 200"
        width={size}
        height={size}
        style={{ position: 'relative', overflow: 'visible', display: 'block' }}
      >
        <defs>
          <linearGradient id={`mascot-head-${uid}`} x1="0.2" y1="0" x2="0.8" y2="1">
            <stop offset="0%" style={{ stopColor: headTop }} />
            <stop offset="100%" style={{ stopColor: headBottom }} />
          </linearGradient>
          <clipPath id={`mascot-clip-${uid}`}>
            <path d={HEAD_PATH} />
          </clipPath>
        </defs>
        <g ref={headRef} transform={headTransform(pose)} style={{ opacity: faceOpacity(pose) }}>
          <path d={HEAD_PATH} fill={`url(#mascot-head-${uid})`} />
          <g clipPath={`url(#mascot-clip-${uid})`}>
            <g ref={eyesRef} transform={eyesTransform(pose)}>
              <g ref={arcsRef} opacity={pose.sleep} data-part="closed-eyes">
                {EYES.map((e, i) => (
                  <path
                    key={i}
                    d={arcPath(e)}
                    fill="none"
                    style={{ stroke: eyeColor }}
                    strokeWidth={ARC_STROKE}
                    strokeLinecap="round"
                  />
                ))}
              </g>
              <g ref={openEyesRef} opacity={1 - pose.sleep}>
                {EYES.map((e, i) => (
                  <rect
                    key={i}
                    ref={(el) => {
                      eyeRefs.current[i] = el;
                    }}
                    x={e.cx - EYE_W / 2}
                    y={e.cy - EYE_H / 2}
                    width={EYE_W}
                    height={EYE_H}
                    rx={EYE_W / 2}
                    style={{ fill: eyeColor }}
                    transform={eyeTransform(pose, i)}
                  />
                ))}
              </g>
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
};
